import React from 'react';
import { Link } from 'react-router-dom';
import stravaLogo from '../img/api_logo_cptblWith_strava_stack_light.svg';
import connectedLogo from '../img/connected.png';
import garminLogo from '../img/garmin_logo_white.png';

const AboutPageEn = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-100 to-orange-0">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">About Map Prints</h1>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Strava Heatmaps, Garmin Maps & GPX Posters</h2>
          <p className="text-gray-700 mb-4">
            Map Prints is your go-to platform for creating beautiful, personalized Strava heatmaps, Garmin activity maps, and GPX poster prints. Whether you're a runner, hiker, cyclist, or outdoor enthusiast, our tool transforms your activity data into stunning, printable map posters.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Supported Data Sources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center">
              <img src={stravaLogo} alt="Strava" className="h-16 mb-4" />
              <h3 className="font-bold mb-2">Strava Heatmap</h3>
              <p className="text-gray-700">Connect your Strava account to build a personal Strava heatmap of your running, cycling, and hiking activities</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <img src={garminLogo} alt="Garmin" className="h-16 mb-4 bg-gray-900 p-2 rounded" />
              <h3 className="font-bold mb-2">Garmin Map</h3>
              <p className="text-gray-700">Import Garmin Connect activity data and turn it into a Garmin heatmap or custom map poster</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="h-16 mb-4 flex items-center justify-center text-2xl font-bold text-gray-900">GPX</div>
              <h3 className="font-bold mb-2">GPX Visualizer</h3>
              <p className="text-gray-700">Upload GPX files to visualize routes and trails or turn a single GPX into a printable poster</p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Custom Colors</h3>
              <p className="text-gray-700">Choose custom colors for activities, buildings, forests, water, and natural areas</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Activity Heatmaps</h3>
              <p className="text-gray-700">Visualize your activity patterns with beautiful heatmaps showing where you've been most active</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Customizable Maps</h3>
              <p className="text-gray-700">Adjust map size, scale, and coverage area to create the perfect print</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">High-Quality Prints</h3>
              <p className="text-gray-700">Export high-resolution maps perfect for framing or home decoration</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Multiple Formats</h3>
              <p className="text-gray-700">Save your maps in various formats and sizes for printing or digital use</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Trail Mapping</h3>
              <p className="text-gray-700">Perfect for hiking maps, running routes, cycling trails, and outdoor adventures</p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What You Can Create</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Personalized hiking maps from your favorite trails</li>
              <li>Running route visualizations with activity intensity</li>
              <li>Cycling maps with custom route highlighting</li>
              <li>Activity heatmaps showing your favorite areas</li>
              <li>Custom trail maps for outdoor exploration</li>
              <li>Printable maps for wall art and decoration</li>
              <li>GPS route visualizations from GPX files</li>
              <li>Sport activity mapping and analysis</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How It Works</h2>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li>Connect your Strava, Garmin account, or upload a GPX file</li>
              <li>Select the location and customize your map colors</li>
              <li>Adjust the map size and coverage area</li>
              <li>Preview your map, and use it as a direct feedback to change colors</li>
              <li>Download and print your personalized map</li>
            </ol>
          </section>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Perfect For</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>Athletes wanting to visualize their performance</li>
            <li>Outdoor enthusiasts creating custom trail maps</li>
            <li>Running and cycling communities</li>
            <li>Fitness trackers and activity loggers</li>
            <li>Gift ideas for active friends and family</li>
            <li>Wall art for home gyms and offices</li>
            <li>Route planning and navigation</li>
          </ul>
        </section>


        <div className="bg-blue-50 p-4 rounded-xl mt-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Ready to Create Your Custom Map?</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/playground"
              className="inline-block px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition"
            >
              Demo Playground
            </Link>
            <span className="text-gray-600 flex items-center">or</span>
            <Link
              to="/login"
              className="inline-block px-6 py-3 border-2 border-gray-900 text-gray-900 rounded-xl hover:bg-gray-100 transition"
            >
              Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutPageDe = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-100 to-orange-0">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Über Map Prints</h1>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Strava Heatmap, Garmin Karte & GPX Poster</h2>
          <p className="text-gray-700 mb-4">
            Map Prints ist deine Plattform zum Erstellen personalisierter Strava Heatmaps, Garmin Karten und GPX Poster. Ob Läufer, Wanderer, Radfahrer oder Outdoor-Enthusiast – Map Prints verwandelt deine Aktivitätsdaten in druckbare Karten und individuelle Wandbilder.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unterstützte Datenquellen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center">
              <img src={stravaLogo} alt="Strava" className="h-16 mb-4" />
              <h3 className="font-bold mb-2">Strava Heatmap</h3>
              <p className="text-gray-700">Verbinde dein Strava-Konto und erstelle deine persönliche Strava Heatmap aus Lauf-, Rad- und Wanderaktivitäten</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <img src={garminLogo} alt="Garmin" className="h-16 mb-4 bg-gray-900 p-2 rounded" />
              <h3 className="font-bold mb-2">Garmin Karte</h3>
              <p className="text-gray-700">Importiere deine Garmin Connect Aktivitätsdaten und erstelle eine Garmin Heatmap oder individuelle Karte als Poster</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="h-16 mb-4 flex items-center justify-center text-2xl font-bold text-gray-900">GPX</div>
              <h3 className="font-bold mb-2">GPX Visualizer</h3>
              <p className="text-gray-700">Lade GPX-Dateien hoch, um Routen und Pfade zu visualisieren oder ein einzelnes GPX als druckbares Poster zu erstellen</p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Funktionen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Benutzerdefinierte Farben</h3>
              <p className="text-gray-700">Wähle benutzerdefinierte Farben für Aktivitäten, Gebäude, Wälder, Wasser und Naturgebiete</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Aktivitäts-Heatmaps</h3>
              <p className="text-gray-700">Visualisiere deine Aktivitätsmuster mit wunderschönen Heatmaps, die zeigen, wo du am aktivsten warst</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Anpassbare Karten</h3>
              <p className="text-gray-700">Passe Kartengröße, Maßstab und Abdeckungsbereich an, um den perfekten Druck zu erstellen</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Hochwertige Drucke</h3>
              <p className="text-gray-700">Exportiere hochauflösende Karten, perfekt zum Einrahmen oder zur Heimdekoration</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Mehrere Formate</h3>
              <p className="text-gray-700">Speichere deine Karten in verschiedenen Formaten und Größen zum Drucken oder digitalen Gebrauch</p>
            </div>
            <div className="bg-white p-4 rounded-xl">
              <h3 className="font-bold text-lg mb-2">Wanderkarten</h3>
              <p className="text-gray-700">Perfekt für Wanderkarten, Laufstrecken, Radstrecken und Outdoor-Abenteuer</p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Was du erstellen kannst</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Personalisierte Wanderkarten von deinen Lieblingswegen</li>
              <li>Laufstrecken-Visualisierungen mit Aktivitätsintensität</li>
              <li>Radfahrkarten mit benutzerdefinierter Routenhervorhebung</li>
              <li>Aktivitäts-Heatmaps, die deine bevorzugten Gebiete zeigen</li>
              <li>Benutzerdefinierte Wanderkarten zum Erkunden im Freien</li>
              <li>Druckbare Karten für Wanddekorationen</li>
              <li>GPS-Routenvisualisierungen aus GPX-Dateien</li>
              <li>Sportaktivitäts-Kartierung und -Analyse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Wie es funktioniert</h2>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li>Verbinde dein Strava-Konto, Garmin oder lade eine GPX-Datei hoch</li>
              <li>Wähle den Standort und passe deine Kartenfarben an</li>
              <li>Passe die Kartengröße und Abdeckungsbereich an</li>
              <li>Schaue dir deine Karte als Vorschau an und nutze die Vorschau um die Karte anzupassen</li>
              <li>Lade deine personalisierte Karte herunter und drucke sie</li>
            </ol>
          </section>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Perfekt für</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>Sportler, die ihre Leistung visualisieren möchten</li>
            <li>Outdoor-Enthusiasten, die benutzerdefinierte Wanderkarten erstellen</li>
            <li>Lauf- und Radfahrer-Communities</li>
            <li>Fitness-Tracker und Aktivitätsprotokollisten</li>
            <li>Geschenkideen für aktive Freunde und Familie</li>
            <li>Wanddekorationen für Home Gyms und Büros</li>
            <li>Routenplanung und Navigation</li>
          </ul>
        </section>


        <div className="bg-blue-50 p-4 rounded-xl mt-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Bereit, deine benutzerdefinierte Karte zu erstellen?</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/playground"
              className="inline-block px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition"
            >
              Demo Playground
            </Link>
            <span className="text-gray-600 flex items-center">oder</span>
            <Link
              to="/login"
              className="inline-block px-6 py-3 border-2 border-gray-900 text-gray-900 rounded-xl hover:bg-gray-100 transition"
            >
              Einloggen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AboutPageEn, AboutPageDe };
